<script setup>
import { onMounted, watch, useTemplateRef, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useContactFilterContext } from 'dashboard/components-next/filter/contactProvider.js';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import Button from 'next/button/Button.vue';
import ConditionRow from 'dashboard/components-next/filter/ConditionRow.vue';

// Reuses the exact same building blocks as the Contacts screen filter
// (ConditionRow + contactProvider + filterQueryGenerator) but stays inline and,
// unlike ContactsFilter.vue, never dispatches contacts/setContactFilters — so it
// does not mutate the Contacts page state while used inside the campaign wizard.
const emit = defineEmits(['update:query']);

const { t } = useI18n();
const store = useStore();
const { filterTypes } = useContactFilterContext();

const DEFAULT_FILTER = {
  attributeKey: 'name',
  filterOperator: 'equal_to',
  values: '',
  queryOperator: 'and',
  attributeModel: 'standard',
};

const rows = defineModel({ type: Array, default: () => [] });

const conditionsRef = useTemplateRef('conditionsRef');

const emitQuery = () => {
  if (!Array.isArray(rows.value) || rows.value.length === 0) {
    emit('update:query', []);
    return;
  }
  const snakeRows = useSnakeCase(JSON.parse(JSON.stringify(rows.value)));
  emit('update:query', filterQueryGenerator(snakeRows).payload);
};

const addFilter = () => {
  rows.value.push({ ...DEFAULT_FILTER });
};

const removeFilter = index => {
  if (rows.value.length <= 1) {
    rows.value = [{ ...DEFAULT_FILTER }];
  } else {
    rows.value.splice(index, 1);
  }
};

const validate = () => {
  const refs = conditionsRef.value;
  if (!refs) return true;
  const list = Array.isArray(refs) ? refs : [refs];
  return list.every(condition => condition.validate());
};

defineExpose({ validate });

onMounted(async () => {
  // Ensure the picker has custom attributes + labels available even when the
  // campaigns page is opened first (the Contacts page usually loads these).
  store.dispatch('attributes/get');
  store.dispatch('labels/get');
  if (!Array.isArray(rows.value) || rows.value.length === 0) {
    rows.value = [{ ...DEFAULT_FILTER }];
  }
  await nextTick();
  emitQuery();
});

watch(rows, emitQuery, { deep: true });
</script>

<template>
  <div class="space-y-3">
    <ul class="grid gap-2 list-none">
      <template v-for="(filter, index) in rows" :key="`condition-${index}`">
        <ConditionRow
          v-if="index === 0"
          ref="conditionsRef"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:values="filter.values"
          :filter-types="filterTypes"
          :show-query-operator="false"
          @remove="removeFilter(index)"
        />
        <ConditionRow
          v-else
          ref="conditionsRef"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:query-operator="rows[index - 1].queryOperator"
          v-model:values="filter.values"
          show-query-operator
          :filter-types="filterTypes"
          @remove="removeFilter(index)"
        />
      </template>
    </ul>
    <Button sm ghost blue class="flex-shrink-0" @click="addFilter">
      {{ t('CAMPAIGN.WHATSAPP_BROADCASTS.WIZARD.CONTACT_FILTERS_ADD') }}
    </Button>
  </div>
</template>
